import type { InvoiceResponse } from './types';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils/logging';

import { zodInvoiceResponseSchema } from './validations';

/**
 * Fetches a single invoice by ID from the API (internal endpoint with additional data).
 *
 * @param invoiceId - The ID of the invoice to fetch.
 * @param options - The options for the request.
 * @returns The invoice data.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/get_invoice_internal_invoice_internal__invoice_id__get
 */
export const fetchInvoice = async (
  invoiceId: UUID,
  options?: KyRequestOptions,
) => {
  const response = await vestigasApi
    .get(ENDPOINT.INVOICE.GET_INTERNAL(invoiceId), options)
    .json<InvoiceResponse | undefined>();

  // Validate the returned data and log any validation errors
  const validatedResponse = zodInvoiceResponseSchema.safeParse(response);

  if (validatedResponse?.error?.issues?.length) {
    log.error({
      message: 'Invoice API response has validation errors',
      error: {
        errors: validatedResponse?.error?.issues,
        errorMessage: validatedResponse?.error?.message,
        response,
      },
    });
  }

  return response;
};
